import {Component, effect, inject, Signal, signal, WritableSignal} from '@angular/core';
import {SidebarComponent, SidebarToggleComponent, SvgSpriteIcon} from '@selpia/shared/ui';
import {FeatureDescriptionComponent} from '@selpia/data-batch/feature-description';
import {FeatureDimensionSelectorComponent} from '@selpia/perspective/feature-dimension-selector';
import {FeatureIncludeTotalComponent} from '@selpia/perspective/feature-include-total';
import {FeatureMeasureSelectorComponent} from '@selpia/options/feature-measure-selector';
import {FeaturePeriodSelectorComponent} from '@selpia/options/feature-period-selector';
import {FeatureSortingSelectorComponent} from '@selpia/options/feature-sorting-selector';
import {FeatureTagsComponent} from '@selpia/perspective/feature-tags';
import {FeatureTreeFiltersComponent} from '@selpia/dimension-items/feature-tree-filters';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {DimensionItemsFacade} from '@selpia/dimension-items/data-access';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {DataBatchFacade} from '@selpia/data-batch/data-access';
import {NavigationService} from '@selpia/data-batch/history-access';
import {DimensionItem, DimensionLevel, Layout, Perspective} from '@selpia/shared/models';
import {PagePath} from '../page-path.model';
import {FeatureTilePreviewComponent} from '@selpia/data-batch/feature-tile-preview';
import {OptionsFacade} from '@selpia/options/data-access';
import {FeatureHighlightSelectorComponent} from '@selpia/options/feature-highlight-selector';
import {MonitoringService} from '@selpia/user/tracking';
import {FeatureDisplaySelectorComponent} from '@selpia/options/feature-display-selector';
import {FeatureInsightsComponent} from '@data-batch/feature-insights';

@Component({
  selector: 'web-tilegrid-view',
  imports: [
    FeatureDescriptionComponent,
    FeatureDimensionSelectorComponent,
    FeatureIncludeTotalComponent,
    FeatureMeasureSelectorComponent,
    FeaturePeriodSelectorComponent,
    FeatureSortingSelectorComponent,
    FeatureTagsComponent,
    FeatureTilePreviewComponent,
    FeatureTreeFiltersComponent,
    SidebarComponent,
    SidebarToggleComponent,
    FeatureHighlightSelectorComponent,
    FeatureDisplaySelectorComponent,
    FeatureInsightsComponent,
  ],
  templateUrl: './tilegrid-view.component.html',
  styleUrl: './tilegrid-view.component.scss',
  providers: [NavigationService],
  host: {
    '[class.dynamic-sidebar]': 'hasDynamicSidebar()',
    '[class.opened-sidebar]': '!sidebarHidden()',
  },
})
export class TilegridViewComponent {
  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #dataBatchFacade = inject(DataBatchFacade);
  readonly #dimensionItemsFacade = inject(DimensionItemsFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #optionsFacade = inject(OptionsFacade);

  readonly #navigationService = inject(NavigationService);
  readonly #monitoringService = inject(MonitoringService);

  protected readonly SvgSpriteIcon = SvgSpriteIcon;

  hasDynamicSidebar: WritableSignal<boolean> = signal(false);
  sidebarHidden: WritableSignal<boolean> = signal(true);

  readonly isAIAssistantEnabled = this.#appConfigFacade.isAIAssistantEnabled;

  protected readonly layout: Signal<Layout> = this.#optionsFacade.layout;

  constructor() {
    this.#setInitialView();

    this.#watchPerspectiveChanges();
    this.#watchColouringStrategy();
  }


  #watchPerspectiveChanges(): void {
    effect(
      () => {
        const perspective: Perspective | undefined = this.#perspectiveFacade.perspective();

        if (perspective) {
          const perspectiveWithoutDate: Perspective = {...perspective, date: undefined};
          this.#dataBatchFacade.loadDataBatch(perspectiveWithoutDate);
        }
      },
    );
  }

  #watchColouringStrategy(): void {
    effect(
      () => {
        const colouringStrategy = this.#optionsFacade.colouringStrategy();
        const key = this.#dataBatchFacade.summarizationKey();

        if (colouringStrategy && key) {
          this.#dataBatchFacade.loadHighlights(key, colouringStrategy);
        }
      },
    );
  }

  #setInitialView(): void {
    const e1 = effect(
      () => {
        const initialLevel: DimensionLevel | undefined = this.#appConfigFacade.initialFocusDimensionLevel();
        const dimensionItems: Map<string, DimensionItem[]> | undefined = this.#dimensionItemsFacade.items();

        if (!initialLevel || !dimensionItems) {
          return;
        }

        const measure = this.#appConfigFacade.initialFocusMeasure();
        const period = this.#appConfigFacade.initialPeriod();
        const date = this.#appConfigFacade.initialDate();

        const colouringStrategy = this.#appConfigFacade.highlightStrategies()[0];

        if (period && date && measure) {
          this.#navigationService.initializeView({
              focusDimensionLevel: initialLevel,
              filterDimensions: [],
              includeTotal: true,
              date,
            },
            {measure, period, colouringStrategy},
          );
          e1.destroy();
        }
      },
      {manualCleanup: true},
    );
    this.#monitoringService.logPageView('Tilegrid', '/tile-grid');
  }

  onSidebarDynamic(dynamicSidebar: boolean) {
    this.hasDynamicSidebar.set(dynamicSidebar);
    if (!dynamicSidebar) {
      setTimeout(() => this.sidebarHidden.set(true), 10);
    }
  }

  protected readonly PagePath = PagePath;
}
